import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    TOUR_STEP_IDS,
    filterStepsByPermissions,
} from "../config/tourSteps";

// Persistencia local do progresso do Tour Guiado, por usuario.
// Chave: tour-guiado:v1:{userId}
//
// Estado de progresso por step:
// - completed_at: visitou a rota-step (D3: acessar = concluido). Conta
//   para o ProgressCard. Setado pelo Wrapper quando location matcha um step.
// - hint_seen_at: fechou o tooltip explicativo (PageHint). Controla quando
//   reabrir o tooltip. Setado pelo PageHint ao fechar.
//
// Comportamento conforme D6:
// - WelcomeModal aparece UMA vez na vida do usuario (gate por welcome_seen).
// - ProgressCard so aparece se o usuario ACEITOU (gate por tour_started).
// - PageHint aparece enquanto tour_active e hint_seen_at estiver nulo.
// - Switch reativando zera apenas hint_seen_at, sem tocar welcome_seen,
//   tour_started nem completed_at.
//
// always_show_hints: ligado quando o usuario reativa o switch DEPOIS de ter
// concluido o tour inicial (todos os 6 steps com completed_at). Enquanto este
// flag estiver true, o PageHint ignora hint_seen_at e abre toda vez que a
// rota-step e visitada — modo "review on demand".
//
// Auto-off: quando o tour inicial e concluido (allCompleted vira true durante
// a primeira passagem, com always_show_hints=false), o tour_active e zerado
// automaticamente para que o switch apareca OFF e o tour pare por completo.

const TOUR_KEY_PREFIX = "tour-guiado:v1:";

const storageKeyFor = (userId) => `${TOUR_KEY_PREFIX}${userId}`;

const buildInitialProgress = () =>
    TOUR_STEP_IDS.reduce(
        (acc, id) => ({
            ...acc,
            [id]: { hint_seen_at: null, completed_at: null },
        }),
        {}
    );

const buildInitialState = () => ({
    welcome_seen: false,
    tour_started: false,
    tour_active: true,
    always_show_hints: false,
    progress: buildInitialProgress(),
});

const normalizeState = (raw) => {
    if (!raw || typeof raw !== "object") return buildInitialState();
    return {
        welcome_seen: !!raw.welcome_seen,
        tour_started: !!raw.tour_started,
        tour_active: raw.tour_active !== false,
        always_show_hints: !!raw.always_show_hints,
        progress: { ...buildInitialProgress(), ...(raw.progress || {}) },
    };
};

const TourGuiadoContext = createContext();

export const TourGuiadoProvider = ({ userId, permissions, children }) => {
    const [state, setState] = useState(buildInitialState());
    const [hydrated, setHydrated] = useState(false);

    // enabledSteps respeita as permissoes do user (companySettings/users/credits).
    // Steps que o user nao pode acessar saem do tour: nao contam pro
    // ProgressCard, allCompleted nem auto-off. Steps no localStorage
    // permanecem (caso a permissao volte algum dia).
    const enabledSteps = useMemo(
        () => filterStepsByPermissions(permissions),
        [permissions]
    );
    const enabledStepIds = useMemo(
        () => enabledSteps.map((s) => s.id),
        [enabledSteps]
    );

    // Hidratacao quando o userId fica disponivel.
    useEffect(() => {
        if (!userId) {
            setHydrated(false);
            return;
        }
        try {
            const raw = localStorage.getItem(storageKeyFor(userId));
            setState(raw ? normalizeState(JSON.parse(raw)) : buildInitialState());
        } catch (e) {
            setState(buildInitialState());
        }
        setHydrated(true);
    }, [userId]);

    // Persistencia.
    useEffect(() => {
        if (!userId || !hydrated) return;
        try {
            localStorage.setItem(storageKeyFor(userId), JSON.stringify(state));
        } catch (e) {
            // Se localStorage estiver cheio ou bloqueado, falhar silencioso.
        }
    }, [state, userId, hydrated]);

    // Sincronia entre tabs do mesmo usuario.
    useEffect(() => {
        if (!userId) return undefined;
        const key = storageKeyFor(userId);
        const handler = (event) => {
            if (event.key !== key) return;
            if (!event.newValue) {
                setState(buildInitialState());
                return;
            }
            try {
                setState(normalizeState(JSON.parse(event.newValue)));
            } catch (e) {
                /* noop */
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, [userId]);

    // Acoes idempotentes — todas usam setState((prev) => ...) para evitar
    // race entre dispatches concorrentes (ex: navegacao rapida marcando varios steps).

    // Marca como concluido (D3: acessar = concluido). Idempotente.
    const markStepCompleted = useCallback((stepId) => {
        if (!stepId) return;
        setState((prev) => {
            if (!prev.progress[stepId]) return prev;
            if (prev.progress[stepId].completed_at) return prev;
            return {
                ...prev,
                progress: {
                    ...prev.progress,
                    [stepId]: {
                        ...prev.progress[stepId],
                        completed_at: new Date().toISOString(),
                    },
                },
            };
        });
    }, []);

    // Marca o tooltip como visto (controla reabertura). Idempotente.
    const markStepHintSeen = useCallback((stepId) => {
        if (!stepId) return;
        setState((prev) => {
            if (!prev.progress[stepId]) return prev;
            if (prev.progress[stepId].hint_seen_at) return prev;
            return {
                ...prev,
                progress: {
                    ...prev.progress,
                    [stepId]: {
                        ...prev.progress[stepId],
                        hint_seen_at: new Date().toISOString(),
                    },
                },
            };
        });
    }, []);

    const dismissWelcome = useCallback((accepted) => {
        setState((prev) => ({
            ...prev,
            welcome_seen: true,
            tour_started: !!accepted,
            // Pular: tour_active=false (switch fica OFF). Comecar tour: mantem
            // tour_active=true (default). User pode reativar pelo switch depois.
            tour_active: !!accepted,
        }));
    }, []);

    const setTourActive = useCallback((active) => {
        setState((prev) => {
            if (active) {
                // Reativar: zera apenas hint_seen_at, preserva completed_at — D6.
                const resetProgress = Object.keys(prev.progress).reduce(
                    (acc, id) => ({
                        ...acc,
                        [id]: {
                            hint_seen_at: null,
                            completed_at: prev.progress[id].completed_at,
                        },
                    }),
                    {}
                );
                // Se o tour inicial ja foi concluido, esta reativacao e modo
                // "review on demand": tooltip aparece toda vez que o user
                // visita a rota-step (PageHint ignora hint_seen_at).
                // Considera apenas steps que o user pode acessar.
                const allDone =
                    enabledStepIds.length > 0 &&
                    enabledStepIds.every(
                        (id) => prev.progress[id]?.completed_at
                    );
                return {
                    ...prev,
                    tour_active: true,
                    always_show_hints: allDone,
                    progress: resetProgress,
                };
            }
            return { ...prev, tour_active: false, always_show_hints: false };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledStepIds]);

    const allCompleted = useMemo(
        () =>
            enabledStepIds.length > 0 &&
            enabledStepIds.every(
                (id) => state.progress[id] && state.progress[id].completed_at
            ),
        [state.progress, enabledStepIds]
    );

    // Auto-off ao concluir o tour inicial: quando o usuario passa pelos 6 steps
    // pela primeira vez (always_show_hints=false), desliga o switch para que o
    // tour pare por completo. Em modo review (always_show_hints=true) nao roda,
    // pois o usuario optou por ver os tooltips de novo.
    //
    // Espera ate que TODOS os hints tenham sido vistos (hint_seen_at) — caso
    // contrario, o auto-off rodaria entre o markStepCompleted (acesso a rota)
    // e o setOpen do PageHint (proximo tick), fechando o modal antes de aparecer.
    // Isso era visivel no ultimo step (Creditos): completed_at marcado, allCompleted
    // virava true, tour_active=false antes do PageHint abrir.
    useEffect(() => {
        if (!hydrated) return;
        if (!allCompleted) return;
        if (state.always_show_hints) return;
        if (!state.tour_active) return;
        if (enabledStepIds.length === 0) return;
        const allHintsSeen = enabledStepIds.every(
            (id) => state.progress[id]?.hint_seen_at
        );
        if (!allHintsSeen) return;
        setState((prev) => {
            if (!prev.tour_active) return prev;
            if (prev.always_show_hints) return prev;
            return { ...prev, tour_active: false };
        });
    }, [
        allCompleted,
        hydrated,
        state.always_show_hints,
        state.tour_active,
        state.progress,
        enabledStepIds,
    ]);

    const value = useMemo(
        () => ({
            state,
            hydrated,
            allCompleted,
            enabledSteps,
            enabledStepIds,
            markStepCompleted,
            markStepHintSeen,
            dismissWelcome,
            setTourActive,
        }),
        [
            state,
            hydrated,
            allCompleted,
            enabledSteps,
            enabledStepIds,
            markStepCompleted,
            markStepHintSeen,
            dismissWelcome,
            setTourActive,
        ]
    );

    return (
        <TourGuiadoContext.Provider value={value}>
            {children}
        </TourGuiadoContext.Provider>
    );
};

export const useTourGuiado = () => {
    const ctx = useContext(TourGuiadoContext);
    if (!ctx) {
        // Provider ausente — devolve no-op seguro pra nao quebrar render.
        return {
            state: buildInitialState(),
            hydrated: false,
            allCompleted: false,
            enabledSteps: [],
            enabledStepIds: [],
            markStepCompleted: () => {},
            markStepHintSeen: () => {},
            dismissWelcome: () => {},
            setTourActive: () => {},
        };
    }
    return ctx;
};

export default TourGuiadoContext;
